// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useSession } from '@common/authorization/context/Session';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { ConfigProvider, type ConfigProviderProps } from 'antd';
import { type PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import PageFooter from '../components/layout/PageFooter';
import { PORTAL_IDLE_TIMEOUT, PortalPath, type RouteArea, routeAreaOf } from './constants';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { useLocalLogout, useLogout } from './hooks/useLogout';
import { PortalHeader, TenantType } from './PortalHeader';
import './PortalLayout.less';

export type PortalSurface = 'home' | 'settings' | 'eamc' | 'page';

const areaSurface: Record<RouteArea, PortalSurface> = { global: 'settings', eamc: 'eamc' };

const surfaceOf = (pathname: string): PortalSurface => {
  if (pathname === PortalPath.HOME) {
    return 'home';
  }
  const area = routeAreaOf(pathname);
  return area ? areaSurface[area] : 'page';
};

const contentClass: Record<PortalSurface, string> = {
  home: 'home-content',
  settings: 'scroll-y-off',
  eamc: 'scroll-y-off',
  page: 'content',
};

const HUB_FOOTER_TITLE = 'portal.info.title';

// Global settings renders its own footer in its content column, so its nav can run to the bottom of the window.
const footerTitle: Record<Exclude<PortalSurface, 'settings'>, string> = {
  home: HUB_FOOTER_TITLE,
  eamc: 'portal.eamc.footer-title',
  page: HUB_FOOTER_TITLE,
};

// Modals and dropdowns render outside .portal-layout, so they need their own hook for the portal's text styles.
const OVERLAYS: ConfigProviderProps = {
  modal: { className: 'portal-overlay' },
  dropdown: { className: 'portal-overlay' },
};

const PortalLayout = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const navigateKeepSearch = useNavigateKeepSearch();
  const location = useLocation();

  const { tenantType } = useSession();

  const userLogout = useLogout();
  const localLogout = useLocalLogout();

  const handleSettings = () => {
    navigateKeepSearch(PortalPath.SETTINGS);
  };

  const handleLogout = async () => {
    if (tenantType === TenantType.TEST) {
      await localLogout();
    } else {
      await userLogout();
    }
  };

  const { startIdleTimer, stopIdleTimer } = useIdleTimeout(PORTAL_IDLE_TIMEOUT, handleLogout);

  useEffect(() => {
    startIdleTimer();
    return () => {
      stopIdleTimer();
    };
  }, [startIdleTimer, stopIdleTimer]);

  const surface = surfaceOf(location.pathname);

  return (
    <ConfigProvider {...OVERLAYS}>
      <div className="portal-layout">
        {/* Header */}
        <PortalHeader userLogout={handleLogout} handleSettings={handleSettings} surface={surface} />

        {/* Main content */}
        <div className={`layout-content ${contentClass[surface]}`}>{children}</div>

        {/* Footer */}
        {surface !== 'settings' && <PageFooter title={t(footerTitle[surface])} />}
      </div>
    </ConfigProvider>
  );
};

export default PortalLayout;
